import { useCallback, useEffect, useMemo } from 'react';
import { Typography, Tabs } from '../../ui';
import { useSearchParams } from 'react-router-dom';
import { usePermission } from '../../store/auth';
import { RetailersListPage } from '../retailers/RetailersListPage';
import { FarmersListPage } from '../farmers/FarmersListPage';
import { UsersPage } from '../admin/UsersPage';
import { FarmerVerifyTab } from '../farmers/FarmerVerifyTab';
import { FarmerSignupsTab } from '../farmers/FarmerSignupsTab';
import { useBadges } from '../../hooks/useBadges';

/**
 * Unified "People" page — Retailers, Farmers/Customers and Users in one place via tabs.
 * Each tab is permission-gated and hosts the existing list page (embedded, so its own
 * title is hidden). The active tab is reflected in the URL (?tab=) so it's linkable and
 * the old /retailers, /farmers, /admin/users routes can redirect straight to the right tab.
 */
export function PeoplePage() {
  const [sp, setSp] = useSearchParams();
  const canRetailers = usePermission('RETAILER.VIEW');
  const canFarmers = usePermission('FARMER.VIEW');
  const canUsers = usePermission('USER.VIEW');
  // Farmer self-onboarding: the sales person's "to verify" list and the office's call list.
  const canVerify = usePermission('FARMER.VERIFY');
  const canSignups = usePermission('FARMER.SIGNUP.MANAGE');
  const badges = useBadges();
  const withCount = (label: string, n?: number) => (n ? `${label} (${n})` : label);

  const items = useMemo(
    () =>
      [
        canRetailers && { key: 'retailers', label: 'Retailers', children: <RetailersListPage embedded /> },
        canFarmers && { key: 'farmers', label: 'Farmers / Customers', children: <FarmersListPage embedded /> },
        canUsers && { key: 'users', label: 'Staffs', children: <UsersPage embedded /> },
        canVerify && { key: 'verify', label: withCount('Farmers to verify', badges?.farmersToVerify), children: <FarmerVerifyTab /> },
        canSignups && { key: 'signups', label: withCount('Incomplete signups', badges?.farmerSignupsToCall), children: <FarmerSignupsTab /> },
      ].filter(Boolean) as { key: string; label: string; children: JSX.Element }[],
    [canRetailers, canFarmers, canUsers, canVerify, canSignups, badges?.farmersToVerify, badges?.farmerSignupsToCall],
  );

  const requested = sp.get('tab');
  const activeKey = items.some((t) => t.key === requested) ? requested! : items[0]?.key;

  /*
   * Normalise the URL when the requested tab is missing/invalid (e.g. a stale deep link, or one the
   * user lacks permission for) so it stays shareable.
   *
   * Both writes REBUILD the params rather than replacing them wholesale. Assigning `{ tab }` dropped
   * every other param, which silently killed row deep-links: a link to
   * `?tab=farmers&focus=1234` arrived, this effect rewrote it to `?tab=farmers`, and the register
   * opened at the top with no idea which farmer had been asked for.
   */
  const setTab = useCallback((k: string) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', k);
      return next;
    }, { replace: true });
  }, [setSp]);

  useEffect(() => {
    if (activeKey && requested !== activeKey) setTab(activeKey);
  }, [activeKey, requested, setTab]);

  /*
   * A tab CLICK is a different act from the normalisation above, now that each register keeps its
   * filters in the query string: the three tabs share this one URL, so carrying the params across
   * would apply a retailer's `status=ACTIVE` to farmers, and show a register the user never filtered
   * as filtered. So an explicit switch starts the next register clean — keeping only what identifies
   * the view itself (`tab`) and the row a deep link asked for (`focus`).
   */
  const onTabClick = useCallback((k: string) => {
    setSp((prev) => {
      const next = new URLSearchParams();
      next.set('tab', k);
      const focus = prev.get('focus');
      if (focus) next.set('focus', focus);
      return next;
    }, { replace: true });
  }, [setSp]);

  return (
    <div>
      <Typography.Title level={1}>People</Typography.Title>
      <Tabs
        activeKey={activeKey}
        onChange={onTabClick}
        items={items}
      />
    </div>
  );
}
